import React, { useCallback, useRef, useState } from "react";
import { useDependencyInjector } from "@whatslly/commons/di";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import styles from "./CreateNewOrg.module.scss";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { FormTextArea } from "@whatslly/ui-components/v2/FormElements/FormTextArea";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { FormCreateAbleAsyncSelect } from "@whatslly/ui-components/v2/FormElements/FormCreateableAsyncSelect";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { Field, FieldProps, FormikErrors, FormikProps } from "formik";
import { getJSON } from "@whatslly/commons/apiUtils";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import type { SearchAccountsFromSuperAdminResponseType } from "@whatslly/types/GetAccountsFromSuperAdminResponse";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { ORG_TYPE_OPTIONS } from "@whatslly/commons/constants/organizationTypes";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { QuestionMarkIcon } from "@whatslly/ui-components/QuestionMarkIcon";
import { securityDestinations } from "../../pages/Destinations/utils/securityDestinations";
import classnames from "classnames";

const SECURITY_STACK_OTHER_VALUE = "other";

const NEW_ITEM_KEY = "NEW_ITEM";

export type CreateNewOrgModalResponse = {
  name: string;
  accountId: string | undefined;
  serverLocation: string;
  orgLanguage: string;
  orgType: string;
  comment?: string;
  data: { email: string; firstName?: string; lastName?: string }[];
  crmOrgId?: string;
  crmFieldValue?: string;
  crmAvailable?: string;
  securityStack: { label: string; value: string }[];
};

type CreateNewOrgModalProps = IModalInstance<CreateNewOrgModalResponse>;

const SERVER_LOCATION_OPTIONS = [
  { label: "Brazil", value: "br1" },
  { label: "US East", value: "us1" },
  { label: "EU Central", value: "eu1" },
];

const ORG_LANGUAGE_OPTIONS = [
  { label: "English", value: "en" },
  { label: "Español", value: "es" },
  { label: "Português", value: "pt" },
];

// Derived from the shared securityDestinations source so the list stays single-sourced with the
// destinations page. "Other" is pushed to the end so it always appears last in the select.
export const SECURITY_STACK_OPTIONS = securityDestinations
  .map((d) => ({ label: d.name, value: d.type }))
  .sort((a, b) => Number(a.value === SECURITY_STACK_OTHER_VALUE) - Number(b.value === SECURITY_STACK_OTHER_VALUE));

const AVAILABLE_CRM_OPTIONS = [
  { label: "Salesforce", value: "salesforce" },
  { label: "Dynamics", value: "dynamics" },
  { label: "Hubspot", value: "hubspot" },
  { label: "Pipedrive", value: "pipedrive" },
];

const stripOrgTypePrefix = (existingName: string) => {
  const allowed = ORG_TYPE_OPTIONS.map((o) => o.value).join("|");
  const regex = new RegExp(`^\\[(${allowed})\\]\\s*`, "i");
  return existingName.replace(regex, "");
};

const addOrgTypePrefix = (baseName: string, type?: { label: string; value: string }) => {
  const cleanName = stripOrgTypePrefix(baseName);
  return type ? `[${type.value}] ${cleanName}` : cleanName;
};

type FormDataType = {
  name: string;
  accountName: null;
  serverLocation: null | { label: string; value: string };
  orgType: null | { label: string; value: string };
  orgLanguage: null | { label: string; value: string };
  crmAvailable: null | { label: string; value: string };
  securityStack: { label: string; value: string }[];
  securityStackOther: string;
  email: string;
  firstName: string;
  lastName: string;
  comment: string;
  crmOrgId?: string;
  crmFieldValue?: string;
};

const BASE_DATA: FormDataType = {
  name: "",
  accountName: null,
  serverLocation: null,
  orgType: null,
  orgLanguage: null,
  crmAvailable: null,
  securityStack: [],
  securityStackOther: "",
  email: "",
  firstName: "",
  lastName: "",
  comment: "",
  crmOrgId: undefined,
  crmFieldValue: undefined,
};

export const CreateNewOrgModal: React.FC<CreateNewOrgModalProps> = observer(({ closeModal }) => {
  const { t } = useTranslation();
  const logger = useDependencyInjector<FrontLogger>(FrontLogger);
  const [selectAccount, setSelectAccount] = useState<{ label: string; value: string }>();
  const formikContext = useRef<FormikProps<FormDataType>>(null);

  const validate = async (values: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values.name) errors.name = t("field_is_required");
    if (!values.serverLocation) errors.serverLocation = t("field_is_required");
    if (!values.firstName) errors.firstName = t("field_is_required");
    if (!values.lastName) errors.lastName = t("field_is_required");
    if (!values.email) errors.email = t("field_is_required");
    if (!values.orgType) errors.orgType = t("field_is_required");
    if (!values.orgLanguage) errors.orgLanguage = t("field_is_required");
    if (!values.securityStack?.length) errors.securityStack = t("field_is_required");
    if (values.securityStack?.some((s) => s.value === SECURITY_STACK_OTHER_VALUE) && !values.securityStackOther?.trim())
      errors.securityStackOther = t("field_is_required");

    return errors;
  };

  const onSubmit = useCallback(
    (formData: FormDataType) => {
      const name = formData.name.trim();
      const normalizedEmail = formData.email.trim().toLowerCase();
      const firstName = formData.firstName.trim();
      const lastName = formData.lastName.trim();
      if (
        !name ||
        !formData.serverLocation ||
        !formData.orgLanguage ||
        !formData.orgType ||
        !normalizedEmail ||
        !firstName ||
        !lastName ||
        !formData.securityStack?.length
      ) {
        return;
      }
      const trimmedOther = formData.securityStackOther.trim();
      const data = [
        {
          email: normalizedEmail,
          firstName: firstName,
          lastName: lastName,
        },
      ];
      const submitData = {
        name,
        accountId: selectAccount?.value,
        serverLocation: formData.serverLocation.value,
        orgLanguage: formData.orgLanguage.value,
        orgType: formData.orgType.value,
        comment: formData.comment?.trim() || undefined,
        crmOrgId: formData.crmOrgId?.trim() || undefined,
        crmFieldValue: formData.crmFieldValue?.trim() || undefined,
        crmAvailable: formData.crmAvailable?.value || undefined,
        securityStack: formData.securityStack.map((s) =>
          s.value === SECURITY_STACK_OTHER_VALUE ? { label: SECURITY_STACK_OTHER_VALUE, value: trimmedOther } : s
        ),
        data,
      };
      closeModal(submitData);
    },
    [closeModal, selectAccount?.value]
  );

  const loadAccountOptions = useCallback(
    async (
      inputValue: string
    ): Promise<{
      options: { label: string; value: string }[];
    }> => {
      if (inputValue.length === 1) {
        return { options: [] };
      }
      const urlSearchParams = new URLSearchParams({ inputValue });
      const urlSuffix = `/integrations/api/search_account_of_super_admin?${urlSearchParams.toString()}`;
      const res = await getJSON({
        urlSuffix,
      }).then((data) => parseOrReturnError<SearchAccountsFromSuperAdminResponseType>(data, logger));
      if (!res || !res.success) {
        return {
          options: [],
        };
      }
      return {
        options: res.accounts.map((account: any) => {
          return { label: account.Name, value: account.Id };
        }),
      };
    },
    []
  );

  return (
    <BaseModal
      className={styles.createOrgModal}
      bodyClassName={styles.body}
      closeOnClickOutside={false}
      closeOnEscape={true}
      closeModal={() => closeModal()}
      header={
        <div className={styles.wrapper}>
          <ModalHeader closeModal={() => closeModal()}>{t("user_management.invite_user_modal.new_org")}</ModalHeader>
        </div>
      }
    >
      <FormElement validate={validate} initialValues={BASE_DATA} formikRef={formikContext} onSubmit={onSubmit}>
        <div className={classnames(styles.section)}>
          <FormTextInput
            paddingInline="none"
            name="name"
            placeholder={t("user_management.invite_user_modal.org_name")}
            data-testid="org-name"
            required
            onChange={(e) => {
              formikContext.current?.setFieldValue(
                "name",
                addOrgTypePrefix(e.target.value ?? "", formikContext.current?.values.orgType || undefined)
              );
            }}
          />
          <FormCreateAbleAsyncSelect<{ label: string; value: string }>
            paddingInline="none"
            value={selectAccount || null}
            required={false}
            data-testid="form-account-select"
            isValidNewOption={() => true}
            onChange={(item: { label: string; value: string }) => {
              setSelectAccount({ label: item.label, value: item.value });
            }}
            onCreateOption={() => {
              setSelectAccount({ label: `Create New`, value: NEW_ITEM_KEY });
            }}
            loadOptionsFromServerFunction={loadAccountOptions}
            placeholder={t("actions_find_accounts")}
            labelText=""
            name="accountName"
          />
          <FormSelect
            placeholder={t("user_management.invite_user_modal.server_location")}
            options={SERVER_LOCATION_OPTIONS}
            name="serverLocation"
            required
            data-testid="server-location-select"
            paddingInline="none"
          />
          <FormSelect
            placeholder={t("user_management.invite_user_modal.org_type")}
            options={ORG_TYPE_OPTIONS}
            onChange={(value: (typeof ORG_TYPE_OPTIONS)[number]) => {
              formikContext.current?.setFieldValue("name", addOrgTypePrefix(formikContext.current?.values.name ?? "", value || undefined));
            }}
            name="orgType"
            required
            data-testid="org-type-select"
            paddingInline="none"
          />
          <FormSelect
            placeholder={t("user_management.invite_user_modal.org_language")}
            options={ORG_LANGUAGE_OPTIONS}
            name="orgLanguage"
            required
            data-testid="org-language-select"
            paddingInline="none"
          />
          <FormSelect
            placeholder={t("user_management.invite_user_modal.available_crm")}
            options={AVAILABLE_CRM_OPTIONS}
            name="crmAvailable"
            data-testid="crm-available-select"
            paddingInline="none"
            showClear
            onChange={() => {
              // Formik keeps crmOrgId/crmFieldValue across CRM changes (and after they unmount), so reset them
              // on any change — otherwise a value entered for one provider leaks to another or submits with no CRM.
              formikContext.current?.setFieldValue("crmOrgId", undefined);
              formikContext.current?.setFieldValue("crmFieldValue", undefined);
            }}
          />
          <FormSelect<string, true>
            placeholder={`${t("user_management.invite_user_modal.security_stack")} *`}
            options={SECURITY_STACK_OPTIONS}
            name="securityStack"
            isMulti={true}
            required
            data-testid="security-stack-select"
            paddingInline="none"
            menuPortalTarget={document.body}
          />
          <Field
            name="securityStack"
            children={(fieldData: FieldProps<{ label: string; value: string }[], FormDataType>) => {
              const hasOther = fieldData.field.value?.some((s) => s.value === SECURITY_STACK_OTHER_VALUE);
              return hasOther ? (
                <FormTextInput
                  name="securityStackOther"
                  type="text"
                  placeholder={t("user_management.invite_user_modal.security_stack_other")}
                  data-testid="security-stack-other"
                  paddingInline="none"
                  required
                />
              ) : null;
            }}
          />
        </div>

        <div>
          <Field
            name="crmOrgId"
            children={(fieldData: FieldProps<string, FormDataType>) => {
              return (
                (fieldData.form?.values["crmAvailable"]?.value === "salesforce" || fieldData.form?.values["crmAvailable"]?.value === "dynamics") && (
                  <div>
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <div className={classnames(styles.sectionTitle)}>CRM Org Id</div>
                    <div>
                      <FormTextInput name="crmOrgId" type="text" placeholder="Org Id" data-testid="crm-org-id" paddingInline="none" />
                    </div>
                  </div>
                )
              );
            }}
          />

          <Field
            name="crmFieldValue"
            children={(fieldData: FieldProps<string, FormDataType>) => {
              return (
                (fieldData.form?.values["crmAvailable"]?.value === "salesforce" || fieldData.form?.values["crmAvailable"]?.value === "dynamics") && (
                  <div>
                    {}
                    <div className={classnames(styles.sectionTitle, styles.sectionTitleWithTooltip)}>
                      <div>CRM Field Value</div>
                      <Tooltip
                        value="Optional. Used to route to this Tuvis org when multiple Tuvis orgs share the same CRM Org Id (e.g., a Business Unit identifier)."
                        lineWrap
                      >
                        <span>
                          <QuestionMarkIcon />
                        </span>
                      </Tooltip>
                    </div>
                    <div>
                      <FormTextInput name="crmFieldValue" type="text" placeholder="Field Value" data-testid="crm-field-value" paddingInline="none" />
                    </div>
                  </div>
                )
              );
            }}
          />
        </div>

        <div className={classnames(styles.sectionTitle)}>{t("user_management.invite_user_modal.subtitle")}</div>
        <div className={classnames(styles.section)}>
          <FormTextInput name="email" type="email" placeholder={t("edit_user.email")} data-testid="user-email" paddingInline="none" required />
          <FormTextInput
            name="firstName"
            type="text"
            placeholder={t("edit_user.first_name")}
            data-testid="user-first-name"
            paddingInline="none"
            required
          />
          <FormTextInput
            name="lastName"
            type="text"
            placeholder={t("edit_user.last_name")}
            data-testid="user-last-name"
            paddingInline="none"
            required
          />
        </div>
        <FormTextArea
          labelText=""
          name="comment"
          placeholder={t("user_management.invite_user_modal.comment_placeholder")}
          data-testid="org-comment"
          paddingInline="none"
          required={false}
          marginBottom="none"
        />
      </FormElement>
      <div className={styles.wrapper}>
        <ModalButtons testId={"modal-buttons-submit-button"} onCancel={() => closeModal()} onSubmit={() => formikContext.current?.submitForm()} />
      </div>
    </BaseModal>
  );
});
